import api from './api'

/** localStorage + cookie key for last-touch referral attribution. */
const REF_STORAGE_KEY = 'zimark_ref'

/**
 * Last-touch attribution TTL (90 days).
 * New `?ref=` overwrites and resets the clock. Survives browser close via localStorage;
 * cookie mirror helps if localStorage is briefly unavailable.
 */
export const REFERRAL_ATTRIBUTION_TTL_MS = 90 * 24 * 60 * 60 * 1000
const REF_COOKIE_MAX_AGE_SEC = Math.floor(REFERRAL_ATTRIBUTION_TTL_MS / 1000)

type StoredReferralPayload = {
  code: string
  /** Epoch ms when this code was last written (last-touch). */
  capturedAt: number
}

export interface ReferralProgramSummary {
  enabled: boolean
  global_commission_percent: number
  hold_window_days: number
  min_payout_cents: number
}

export interface ReferralPayoutProfile {
  required: boolean
  has_legal_name: boolean
  has_id_document: boolean
  complete: boolean
  first_name?: string
  last_name?: string
  id_status?: string | null
  id_document_type?: string | null
  id_country_issued?: string | null
  id_number_masked?: string | null
}

export interface AdminReferrerOverride {
  user_id: string
  commission_percent: number | null
  notes: string | null
  updated_at?: string | null
  has_override: boolean
}

export interface AdminAffiliateFinancial {
  user_id: string
  code: string
  referral_link_path: string
  global_commission_percent: number
  effective_rate_percent: number
  override: {
    user_id: string
    commission_percent: number
    notes: string | null
    updated_at: string | null
  } | null
  available_credit_cents: number
  pending_commission_cents: number
  min_payout_cents: number
  payout_profile?: ReferralPayoutProfile
  attribution_count: number
  attributions: Array<{
    id: string
    invitee_id: string
    invitee_email: string
    invitee_name: string
    status: string
    code_used: string
    commission_end_mode: string
    commission_ends_at: string | null
    created_at: string | null
  }>
  commission_events: Array<{
    id: string
    stripe_invoice_id: string | null
    amount_paid_cents: number
    commission_cents: number
    rate_percent: number
    status: string
    hold_until: string | null
    created_at: string | null
  }>
  payouts: Array<{
    id: string
    amount_cents: number
    method: string
    destination: string | null
    status: string
    admin_note: string | null
    created_at: string | null
    processed_at: string | null
  }>
}

export interface ReferralCodeRules {
  min_length: number
  max_length: number
  charset: string
  cooldown_hours: number
}

export interface ReferralCodePayload {
  code: string
  referral_link_path: string
  is_custom?: boolean
  code_updated_at?: string | null
  code_change_available_at?: string | null
  code_rules?: ReferralCodeRules
}

export interface ReferralDashboard {
  program: ReferralProgramSummary
  code: string
  referral_link_path: string
  is_custom?: boolean
  code_updated_at?: string | null
  code_change_available_at?: string | null
  code_rules?: ReferralCodeRules
  effective_rate_percent: number
  available_credit_cents: number
  pending_commission_cents: number
  min_payout_cents: number
  was_referred?: boolean
  payout_profile?: ReferralPayoutProfile
  invitees: Array<{
    referral_id: string
    invitee_id: string
    invitee_email: string
    invitee_name: string
    invitee_email_masked?: string
    invitee_name_masked?: string
    status: string
    lifecycle_status?: 'registered' | 'purchased' | string
    lifecycle_label?: string
    commission_end_mode: string
    commission_ends_at: string | null
    commission_end_label?: string
    total_commission_cents?: number
    recurring_orders?: Array<{
      commission_cents: number
      amount_paid_cents: number
      status: string
      created_at: string | null
      hold_until: string | null
    }>
    created_at: string | null
  }>
  commission_events: Array<{
    id: string
    stripe_invoice_id: string | null
    amount_paid_cents: number
    commission_cents: number
    rate_percent: number
    status: string
    hold_until: string | null
    created_at: string | null
  }>
  payouts: Array<{
    id: string
    amount_cents: number
    method: string
    destination: string | null
    status: string
    admin_note: string | null
    created_at: string | null
    processed_at: string | null
  }>
  ledger: Array<{
    id: string
    amount_cents: number
    reason: string
    created_at: string | null
  }>
}

function isExpired(capturedAt: number, now = Date.now()): boolean {
  return !Number.isFinite(capturedAt) || now - capturedAt > REFERRAL_ATTRIBUTION_TTL_MS
}

function writeReferralCookie(code: string): void {
  if (typeof document === 'undefined') return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `${REF_STORAGE_KEY}=${encodeURIComponent(code)}` +
    `; Path=/; Max-Age=${REF_COOKIE_MAX_AGE_SEC}; SameSite=Lax${secure}`
}

function clearReferralCookie(): void {
  if (typeof document === 'undefined') return
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''
  document.cookie =
    `${REF_STORAGE_KEY}=; Path=/; Max-Age=0; SameSite=Lax${secure}`
}

function readReferralCookie(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(/(?:^|;\s*)zimark_ref=([^;]*)/)
  if (!match) return null
  try {
    return decodeURIComponent(match[1]).trim() || null
  } catch {
    return match[1]?.trim() || null
  }
}

function persistReferral(code: string, capturedAt: number): void {
  const payload: StoredReferralPayload = { code, capturedAt }
  try {
    localStorage.setItem(REF_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    /* private mode / quota — cookie may still work */
  }
  try {
    writeReferralCookie(code)
  } catch {
    /* ignore */
  }
}

function parseStoredPayload(raw: string | null): StoredReferralPayload | null {
  if (!raw?.trim()) return null
  const trimmed = raw.trim()
  // Legacy: plain code string (pre-TTL). Treat as captured now so TTL starts from first read.
  if (!trimmed.startsWith('{')) {
    return { code: trimmed, capturedAt: Date.now() }
  }
  try {
    const parsed = JSON.parse(trimmed) as Partial<StoredReferralPayload>
    const code = typeof parsed.code === 'string' ? parsed.code.trim() : ''
    const capturedAt = typeof parsed.capturedAt === 'number' ? parsed.capturedAt : NaN
    if (!code || !Number.isFinite(capturedAt)) return null
    return { code, capturedAt }
  } catch {
    return null
  }
}

/**
 * Capture `?ref=` from the URL (last-touch: overwrites prior code + resets TTL).
 * If no ref in URL, returns any still-valid stored attribution.
 */
/** Normalize a referral code for storage / signup (matches backend charset rules). */
export function normalizeReferralCode(raw: string): string | null {
  const normalized = (raw || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, '')
    .slice(0, 32)
  return normalized.length >= 1 ? normalized : null
}

export function captureReferralCodeFromUrl(search?: string): string | null {
  if (typeof window === 'undefined') return null
  const params = new URLSearchParams(search ?? window.location.search)
  const ref = normalizeReferralCode(params.get('ref') || '')
  if (ref) {
    persistReferral(ref, Date.now())
    return ref
  }
  return getStoredReferralCode()
}

/** Valid last-touch code, or null if missing/expired. */
export function getStoredReferralCode(): string | null {
  const now = Date.now()
  let raw: string | null = null
  try {
    raw = localStorage.getItem(REF_STORAGE_KEY)
  } catch {
    raw = null
  }
  const local = parseStoredPayload(raw)
  if (local) {
    if (isExpired(local.capturedAt, now)) {
      clearStoredReferralCode()
    } else {
      const code = normalizeReferralCode(local.code)
      if (!code) {
        clearStoredReferralCode()
      } else {
        const wasLegacy = Boolean(raw && !raw.trim().startsWith('{'))
        // Migrate plain codes to JSON+cookie; refresh cookie if missing.
        if (wasLegacy || code !== local.code || !readReferralCookie()) {
          persistReferral(code, wasLegacy ? now : local.capturedAt)
        }
        return code
      }
    }
  }

  // Cookie-only fallback (e.g. localStorage cleared but cookie remained).
  const fromCookie = normalizeReferralCode(readReferralCookie() || '')
  if (fromCookie) {
    persistReferral(fromCookie, now)
    return fromCookie
  }
  return null
}

export function clearStoredReferralCode(): void {
  try {
    localStorage.removeItem(REF_STORAGE_KEY)
  } catch {
    /* ignore */
  }
  try {
    clearReferralCookie()
  } catch {
    /* ignore */
  }
}

export function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

class ReferralService {
  async getProgram(): Promise<ReferralProgramSummary> {
    const res = await api.get<ReferralProgramSummary>('/referrals/program')
    return res.data
  }

  async submitPartnerInquiry(body: {
    name: string
    email: string
    persona: string
    channel?: string
    message: string
    website?: string
  }): Promise<{ success: boolean }> {
    const res = await api.post<{ success: boolean }>('/referrals/partner-inquiry', {
      name: body.name.trim(),
      email: body.email.trim(),
      persona: body.persona,
      channel: body.channel?.trim() || undefined,
      message: body.message.trim(),
      website: body.website || undefined,
    })
    return res.data
  }

  async getDashboard(): Promise<ReferralDashboard> {
    const res = await api.get<ReferralDashboard>('/referrals/me/dashboard')
    return res.data
  }

  async ensureCode(): Promise<ReferralCodePayload> {
    const res = await api.get<ReferralCodePayload>('/referrals/me/code')
    return res.data
  }

  async setCustomCode(code: string): Promise<ReferralCodePayload> {
    const res = await api.put<ReferralCodePayload>('/referrals/me/code', {
      code: code.trim(),
    })
    return res.data
  }

  async sendInviteEmail(email: string, message?: string): Promise<{ success: boolean; code: string }> {
    const res = await api.post<{ success: boolean; code: string }>('/referrals/me/invite-email', {
      email,
      message: message || undefined,
    })
    return res.data
  }

  async applyCode(code: string): Promise<{ success: boolean; referral_id: string; code_used: string }> {
    const res = await api.post<{ success: boolean; referral_id: string; code_used: string }>(
      '/referrals/me/apply-code',
      { code: code.trim() },
    )
    return res.data
  }

  async requestPayout(amountCents: number, method: string, destination?: string) {
    const res = await api.post('/referrals/me/payout', {
      amount_cents: amountCents,
      method,
      destination: destination || undefined,
    })
    return res.data
  }

  // Admin
  async adminGetSettings(): Promise<ReferralProgramSummary & { invite_email_daily_limit?: number }> {
    const res = await api.get('/admin/referrals/settings')
    return res.data
  }

  async adminUpdateSettings(body: {
    enabled: boolean
    global_commission_percent: number
    hold_window_days: number
    min_payout_cents: number
    invite_email_daily_limit: number
  }) {
    const res = await api.put('/admin/referrals/settings', body)
    return res.data
  }

  async adminListPayouts(
    statusFilter?: string,
    opts?: { skip?: number; limit?: number; userId?: string }
  ) {
    const res = await api.get('/admin/referrals/payouts', {
      params: {
        ...(statusFilter ? { status_filter: statusFilter } : {}),
        ...(opts?.userId ? { user_id: opts.userId } : {}),
        skip: opts?.skip ?? 0,
        limit: opts?.limit ?? 500,
      },
    })
    return res.data as Array<{
      id: string
      user_id: string
      user_email: string
      user_name: string
      amount_cents: number
      method: string
      destination: string | null
      status: string
      admin_note: string | null
      created_at: string | null
      processed_at: string | null
    }>
  }

  async adminProcessPayout(
    payoutId: string,
    action: string,
    adminNote?: string,
    adminPassword?: string
  ) {
    const res = await api.post(`/admin/referrals/payouts/${payoutId}/process`, {
      action,
      admin_note: adminNote || undefined,
      admin_password: adminPassword || undefined,
    })
    return res.data
  }

  async adminListReferrals(opts?: { skip?: number; limit?: number }) {
    const res = await api.get('/admin/referrals/referrals', {
      params: {
        skip: opts?.skip ?? 0,
        limit: opts?.limit ?? 500,
      },
    })
    return res.data
  }

  async adminSetCommissionEnd(referralId: string, mode: string, endsAt?: string) {
    const res = await api.put(`/admin/referrals/referrals/${referralId}/commission-end`, {
      mode,
      ends_at: endsAt || null,
    })
    return res.data
  }

  async adminListOverrides() {
    const res = await api.get('/admin/referrals/overrides')
    return res.data
  }

  async adminGetOverride(userId: string): Promise<AdminReferrerOverride> {
    const res = await api.get<AdminReferrerOverride>(`/admin/referrals/overrides/${userId}`)
    return res.data
  }

  async adminSetOverride(userId: string, commissionPercent: number, notes?: string) {
    const res = await api.put('/admin/referrals/overrides', {
      user_id: userId,
      commission_percent: commissionPercent,
      notes: notes || undefined,
    })
    return res.data
  }

  async adminClearOverride(userId: string): Promise<{ user_id: string; cleared: boolean }> {
    const res = await api.delete<{ user_id: string; cleared: boolean }>(
      `/admin/referrals/overrides/${userId}`
    )
    return res.data
  }

  async adminGetUserFinancial(userId: string): Promise<AdminAffiliateFinancial> {
    const res = await api.get<AdminAffiliateFinancial>(
      `/admin/referrals/users/${userId}/financial`
    )
    return res.data
  }
}

export const referralService = new ReferralService()
export default referralService
